import React, { useContext, useEffect, useMemo, useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import "../styles/components/Abonnement.scss";
import BackToTopButton from '../components/BackToTopButton';
import AbonnementScene3D from "../components/AbonnementScene3D";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { auth } from "../config/firebasedeconfig";
import { UserContext } from "../UserContext";
import { useLanguage } from "../contexts/LanguageContext";
import { getMembershipState } from "../utils/membershipUtils";
import { Helmet } from "react-helmet-async";

const Abonnement = () => {
    const { t, currentLanguage } = useLanguage();
    const [billingPeriod, setBillingPeriod] = useState("monthly");
    const [selectedPlan, setSelectedPlan] = useState("cinephile");
    const [paypalClientId, setPaypalClientId] = useState(null);
    const [paypalError, setPaypalError] = useState(null);
    const [paypalLoading, setPaypalLoading] = useState(false);
    // Confirmation avant un changement anticipé de plan payant
    const [showPlanChangeConfirm, setShowPlanChangeConfirm] = useState(false);
    const [pendingPlanChange, setPendingPlanChange] = useState(null);
    // Confirmation avant l'annulation du renouvellement automatique
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);

    // Downgrade Fanatique -> Cinéphile au prochain renouvellement
    const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
    const [downgradePrepared, setDowngradePrepared] = useState(false);
    const [downgradeData, setDowngradeData] = useState(null);
    const [downgradeLoading, setDowngradeLoading] = useState(false);

    // Verrou temporaire : empêche toute vraie souscription publique
    // tant que les offres MovieFrenzy ne sont pas finalisées.
    const [paypalGateUnlocked, setPaypalGateUnlocked] = useState(false);
    const [showPaypalGate, setShowPaypalGate] = useState(false);
    const [paypalGatePassword, setPaypalGatePassword] = useState("");
    const [paypalGateError, setPaypalGateError] = useState(null);
    const [paypalGateLoading, setPaypalGateLoading] = useState(false);

    const { userId, isLoggedIn, userData } = useContext(UserContext);

    const PAYPAL_API_BASE = process.env.REACT_APP_PAYPAL_API_BASE || "/api/paypal";

    // Fonction de test de sécurité pour empêcher le changement malveillant d'abonnement.
    // const testSubscriptionSecurity = async () => {
    //     try {
    //         const user = auth.currentUser;

    //         if (!user) {
    //             window.alert("❌ Aucun utilisateur connecté.");
    //             return;
    //         }

    //         const userRef = doc(db, "users", user.uid);

    //         await updateDoc(userRef, {
    //             "subscription.planId": "fanatic",
    //             "subscription.status": "active",
    //         });

    //         console.error(
    //             "❌ PROBLÈME : le client a réussi à modifier subscription !"
    //         );

    //         window.alert(
    //             "❌ ÉCHEC DE SÉCURITÉ\n\n" +
    //             "Le client React a réussi à modifier l'abonnement dans Firestore."
    //         );
    //     } catch (error) {
    //         console.log(
    //             "✅ SÉCURITÉ OK : modification subscription refusée"
    //         );
    //         console.log(error.code, error.message);

    //         window.alert(
    //             "✅ SÉCURITÉ FIRESTORE OK\n\n" +
    //             "Le client React n'a pas pu modifier subscription.\n\n" +
    //             `Erreur Firebase : ${error.code || "permission-denied"}`
    //         );
    //     }
    // };


    useEffect(() => {
        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        if (prefersReducedMotion) return;

        const elements = Array.from(document.querySelectorAll(".abn-reveal"));
        if (elements.length === 0) return;

        const perSectionIndex = new Map();

        elements.forEach((el) => {
            const section = el.closest("section") ?? el.parentElement ?? document.body;
            const index = perSectionIndex.get(section) ?? 0;
            perSectionIndex.set(section, index + 1);

            const isFaq = el.classList.contains("abn-reveal--faq");

            if (isFaq) {
                const sign = index % 2 === 0 ? 1 : -1; // 1er: droite->gauche, 2e: gauche->droite, 3e: droite->gauche...
                el.style.setProperty("--abn-enter-x", `${sign * 90}px`);
                el.style.setProperty("--abn-hover-x", `${sign * -10}px`);
                el.style.setProperty("--abn-delay", `${index * 1000}ms`);
                return;
            }

            const dir = index % 2 === 0 ? "left" : "right";
            el.style.setProperty("--abn-enter-x", dir === "left" ? "-56px" : "56px");
            el.style.setProperty("--abn-wiggle-sign", dir === "left" ? "-1" : "1");
            el.style.setProperty("--abn-delay", `${Math.min(index * 90, 240)}ms`);
        });

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    entry.target.classList.toggle("is-inview", entry.isIntersecting);
                });
            },
            { threshold: 0.22, rootMargin: "0px 0px -10% 0px" }
        );

        elements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!paypalGateUnlocked) {
            setPaypalClientId(null);
            return;
        }

        let isMounted = true;
        const loadClientId = async () => {
            try {
                const res = await fetch(`${PAYPAL_API_BASE}/client-id`);
                const data = await res.json();
                if (!res.ok) throw new Error(data?.error || t("abonnement.paypal.genericError"));
                if (isMounted) setPaypalClientId(data.clientId);
            } catch (e) {
                if (isMounted) setPaypalError(e.message || t("abonnement.paypal.genericError"));
            }
        };

        loadClientId();
        return () => { isMounted = false; };
    }, [PAYPAL_API_BASE, paypalGateUnlocked]);

    useEffect(() => {
        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        if (prefersReducedMotion) return;

        const section = document.querySelector(".abonnement-benefits");
        if (!section) return;

        const cards = Array.from(section.querySelectorAll(".abn-card.abn-reveal--benefit"));
        if (cards.length === 0) return;

        const SPIN_MS = 875;
        const GAP_MS = 500;
        let hasRun = false;
        let timers = [];

        const clearTimers = () => {
            timers.forEach((t) => window.clearTimeout(t));
            timers = [];
        };

        const reset = () => {
            clearTimers();
            cards.forEach((c) => c.classList.remove("is-spin"));
            hasRun = false;
        };

        const triggerSpin = (card) => {
            card.classList.remove("is-spin");
            void card.offsetWidth;
            card.classList.add("is-spin");

            const onEnd = (e) => {
                if (e.animationName !== "abn-border-spin") return;
                card.classList.remove("is-spin");
            };
            card.addEventListener("animationend", onEnd, { once: true });
        };

        const run = () => {
            if (hasRun) return;
            hasRun = true;
            cards.forEach((card, i) => {
                const delay = i * (SPIN_MS + GAP_MS);
                timers.push(window.setTimeout(() => triggerSpin(card), delay));
            });
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) run();
                    else reset();
                });
            },
            { threshold: 0.35, rootMargin: "0px 0px -10% 0px" }
        );

        observer.observe(section);
        return () => {
            observer.disconnect();
            reset();
        };
    }, []);



    const PERIOD_META = {
        monthly: { label: t("abonnement.billing.monthUnit"), months: 1 },
        quarterly: { label: t("abonnement.billing.threeMonthsUnit"), months: 3 },
        yearly: { label: t("abonnement.billing.yearUnit"), months: 12 },
    };

    const scrollToPlans = () => {
        const el = document.getElementById("plans");
        if (!el) return;

        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        el.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
    };

    const PRICING = {
        free: { monthly: 0, quarterly: 0, yearly: 0 },
        cinephile: {
            monthly: 5.00,
            quarterly: 12.00,  // -20 % sur 3 mois
            yearly: 39.00,     // -35 % sur 12 mois
        },
        fanatic: {
            monthly: 8.00,
            quarterly: 19.99,  // cohérent mais ajustable
            yearly: 62.99,
        },
    };

    const subscription = userData?.subscription;
    const {
        effectiveTier,
        hasPaidAccess,
        hasRemainingPaidPeriod,
        hasActiveAccessOverride,
        accessOverridePlanId,
        accessOverrideUntil
    } = getMembershipState(subscription);
    useEffect(() => {
        if (
            hasPaidAccess &&
            ["cinephile", "fanatic"].includes(effectiveTier)
        ) {
            setSelectedPlan(effectiveTier);
        }
    }, [hasPaidAccess, effectiveTier]);
    const formattedCurrentPeriodEnd = useMemo(() => {
        const rawDate = subscription?.currentPeriodEnd;

        if (!rawDate) return null;

        const date =
            typeof rawDate?.toDate === "function"
                ? rawDate.toDate()
                : new Date(rawDate);

        if (Number.isNaN(date.getTime())) return null;

        const locale =
            currentLanguage?.startsWith("en")
                ? "en-CA"
                : currentLanguage?.startsWith("es")
                    ? "es-ES"
                    : currentLanguage?.startsWith("de")
                        ? "de-DE"
                        : "fr-CA";

        return new Intl.DateTimeFormat(locale, {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(date);
    }, [subscription?.currentPeriodEnd, currentLanguage]);

    const hasScheduledDowngrade =
        subscription?.status === "active" &&
        hasActiveAccessOverride &&
        accessOverridePlanId === "fanatic" &&
        subscription?.planId === "cinephile";

    const formattedAccessOverrideUntil = useMemo(() => {
        const rawDate = accessOverrideUntil;

        if (!rawDate) return null;

        const date =
            typeof rawDate?.toDate === "function"
                ? rawDate.toDate()
                : new Date(rawDate);

        if (Number.isNaN(date.getTime())) return null;

        const locale =
            currentLanguage?.startsWith("en")
                ? "en-CA"
                : currentLanguage?.startsWith("es")
                    ? "es-ES"
                    : currentLanguage?.startsWith("de")
                        ? "de-DE"
                        : "fr-CA";

        return new Intl.DateTimeFormat(locale, {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(date);
    }, [accessOverrideUntil, currentLanguage]);

    const requiresEarlyUpgradeConfirmation = (planKey) => {
        return (
            planKey === "fanatic" &&
            subscription?.planId === "cinephile" &&
            subscription?.status === "cancelled" &&
            hasRemainingPaidPeriod
        );
    };

    const requiresScheduledDowngrade = (planKey) => {
        return (
            planKey === "cinephile" &&
            subscription?.planId === "fanatic" &&
            subscription?.status === "active" &&
            effectiveTier === "fanatic"
        );
    };

    const requestPlanSelection = (planKey) => {
        if (requiresEarlyUpgradeConfirmation(planKey)) {
            setPendingPlanChange(planKey);
            setShowPlanChangeConfirm(true);
            return;
        }

        if (requiresScheduledDowngrade(planKey)) {
            setPaypalError(null);
            setShowDowngradeConfirm(true);
            return;
        }

        setSelectedPlan(planKey);
    };

    const closePlanChangeConfirm = () => {
        setShowPlanChangeConfirm(false);
        setPendingPlanChange(null);
    };

    const confirmPlanChange = () => {
        if (pendingPlanChange) {
            setSelectedPlan(pendingPlanChange);
        }

        setShowPlanChangeConfirm(false);
        setPendingPlanChange(null);
    };

    const planLabelMap = {
        free: t("abonnement.plans.free.name"),
        cinephile: t("abonnement.plans.cinephile.name"),
        fanatic: t("abonnement.plans.fanatic.name")
    };

    const subscriptionStatusLabelMap = {
        active: t("abonnement.status.active"),
        inactive: t("abonnement.status.inactive"),
        none: t("abonnement.status.none"),
        pending: t("abonnement.status.pending"),
        cancelled: t("abonnement.status.cancelled"),
        suspended: t("abonnement.status.suspended"),
        expired: t("abonnement.status.expired")
    };

    const paypalOptions = useMemo(() => {
        if (!paypalClientId) return null;
        return {
            clientId: paypalClientId,
            intent: "subscription",
            vault: true,
            currency: "USD"
        };
    }, [paypalClientId]);

    const ensureAuthToken = async () => {
        if (!auth.currentUser) throw new Error(t("abonnement.paypal.authRequired"));
        return auth.currentUser.getIdToken();
    };

    const openPaypalGate = () => {
        setPaypalGateError(null);
        setShowPaypalGate(true);
    };

    const closePaypalGate = () => {
        if (paypalGateLoading) return;
        setShowPaypalGate(false);
        setPaypalGateError(null);
        setPaypalGatePassword("");
    };

    const verifyPaypalGate = async () => {
        if (!paypalGatePassword.trim() || paypalGateLoading) return;

        setPaypalGateLoading(true);
        setPaypalGateError(null);

        try {
            const response = await fetch(`${PAYPAL_API_BASE}/gate/verify`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    password: paypalGatePassword
                })
            });

            const data = await response.json();

            if (!response.ok || !data?.ok) {
                throw new Error(data?.error || "Mot de passe incorrect.");
            }

            setPaypalGateUnlocked(true);
            setShowPaypalGate(false);
        } catch (error) {
            setPaypalGateError(error?.message || "Impossible de déverrouiller PayPal.");
        } finally {
            setPaypalGateLoading(false);
        }
    };

    const createSubscription = async (planKey) => {
        setPaypalError(null);
        const token = await ensureAuthToken();
        const response = await fetch(`${PAYPAL_API_BASE}/subscribe`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
                "X-MF-PayPal-Gate": paypalGatePassword
            },
            body: JSON.stringify({
                userId,
                planId: planKey,
                billingPeriod,
                returnUrl: `${window.location.origin}/abonnement?sub=success`,
                cancelUrl: `${window.location.origin}/abonnement?sub=cancel`
            })
        });

        const data = await response.json();

        console.log(
            "[PAYPAL] réponse /subscribe :",
            response.status,
            data
        );

        if (!response.ok) {
            throw new Error(
                data?.error ||
                t("abonnement.paypal.genericError")
            );
        }

        return data.subscriptionId;
    };

    const confirmSubscription = async (subscriptionId) => {
        const token = await ensureAuthToken();

        const response = await fetch(`${PAYPAL_API_BASE}/confirm`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                userId,
                subscriptionId
            })
        });

        const data = await response.json();

        console.log(
            "[PAYPAL] réponse /confirm :",
            response.status,
            data
        );

        if (!response.ok) {
            throw new Error(
                data?.error ||
                t("abonnement.paypal.confirmError")
            );
        }

        return data;
    };

    const prepareDowngrade = async () => {
        const token = await ensureAuthToken();

        const response = await fetch(
            `${PAYPAL_API_BASE}/prepare-plan-change`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId,
                    targetPlanId: "cinephile"
                })
            }
        );

        const data = await response.json();

        console.log(
            "[PAYPAL] réponse /prepare-plan-change :",
            response.status,
            data
        );

        if (!response.ok) {
            throw new Error(
                data?.error ||
                t("abonnement.downgrade.error")
            );
        }

        return data;
    };


    const closeDowngradeConfirm = () => {
        if (downgradeLoading) return;

        setShowDowngradeConfirm(false);
    };


    const acceptDowngrade = async () => {
        if (downgradeLoading) return;

        try {
            setDowngradeLoading(true);
            setPaypalError(null);

            const data = await prepareDowngrade();

            if (data?.billingPeriod) {
                setBillingPeriod(data.billingPeriod);
            }

            setDowngradeData(data);
            setDowngradePrepared(true);
            setSelectedPlan("cinephile");
            setShowDowngradeConfirm(false);

        } catch (error) {
            console.error(
                "[PAYPAL] préparation downgrade ERREUR :",
                error
            );

            setPaypalError(
                error?.message ||
                t("abonnement.downgrade.error")
            );
        } finally {
            setDowngradeLoading(false);
        }
    };

    const confirmDowngrade = async (subscriptionId) => {
        const token = await ensureAuthToken();

        const response = await fetch(
            `${PAYPAL_API_BASE}/confirm-plan-change`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    userId,
                    subscriptionId
                })
            }
        );

        const data = await response.json();

        console.log(
            "[PAYPAL] réponse /confirm-plan-change :",
            response.status,
            data
        );

        if (!response.ok) {
            throw new Error(
                data?.error ||
                t("abonnement.downgrade.error")
            );
        }

        return data;
    };

    const openCancelConfirm = () => {
        if (subscription?.status !== "active") return;

        setPaypalError(null);
        setShowCancelConfirm(true);
    };

    const closeCancelConfirm = () => {
        if (cancelLoading) return;

        setShowCancelConfirm(false);
    };

    const cancelCurrentSubscription = async () => {
        if (cancelLoading) return;

        try {
            setCancelLoading(true);
            setPaypalError(null);

            const token = await ensureAuthToken();

            const response = await fetch(
                `${PAYPAL_API_BASE}/cancel-subscription`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        userId
                    })
                }
            );

            const data = await response.json();

            console.log(
                "[PAYPAL] réponse /cancel-subscription :",
                response.status,
                data
            );

            if (!response.ok) {
                throw new Error(
                    data?.error ||
                    t("abonnement.cancelRenewal.error")
                );
            }

            // Ce CustomEvent ne modifie PAS Firestore.
            // Il sert uniquement à synchroniser immédiatement l'interface.
            // La vraie modification vient du backend Firebase Admin.
            window.dispatchEvent(
                new CustomEvent("mf-subscription-updated", {
                    detail: {
                        planId: subscription?.planId,
                        status: "cancelled",
                        currentPeriodEnd:
                            subscription?.currentPeriodEnd ?? null
                    }
                })
            );

            setShowCancelConfirm(false);

        } catch (error) {
            console.error(
                "[PAYPAL] annulation ERREUR :",
                error
            );

            setPaypalError(
                error?.message ||
                t("abonnement.cancelRenewal.error")
            );

        } finally {
            setCancelLoading(false);
        }
    };

    const renderPayPalButtons = (planKey) => {
        if (
            planKey === "cinephile" &&
            downgradePrepared &&
            downgradeData
        ) {
            if (!paypalGateUnlocked) {
                return (
                    <div className="paypal-temp-lock">
                        <button
                            type="button"
                            className="paypal-temp-lock__button"
                            onClick={openPaypalGate}
                        >
                            <strong>PayPal</strong>&nbsp; Confirmer le changement
                        </button>

                        <p className="paypal-hint">
                            {t("abonnement.downgrade.paypalConsent")}
                        </p>
                    </div>
                );
            }

            if (!paypalOptions) {
                return (
                    <p className="paypal-hint">
                        {t("abonnement.paypal.loading")}
                    </p>
                );
            }

            return (
                <div className="paypal-buttons">
                    <PayPalScriptProvider options={paypalOptions}>
                        <PayPalButtons
                            style={{
                                layout: "vertical",
                                shape: "pill",
                                label: "subscribe"
                            }}
                            forceReRender={[
                                downgradeData.subscriptionId,
                                downgradeData.targetPaypalPlanId,
                                paypalClientId
                            ]}
                            createSubscription={(data, actions) => {
                                return actions.subscription.revise(
                                    downgradeData.subscriptionId,
                                    {
                                        plan_id:
                                            downgradeData.targetPaypalPlanId
                                    }
                                );
                            }}
                            onApprove={async (data) => {
                                setPaypalLoading(true);
                                setPaypalError(null);

                                try {
                                    await confirmDowngrade(
                                        downgradeData.subscriptionId
                                    );

                                    setDowngradePrepared(false);
                                    setDowngradeData(null);

                                } catch (error) {
                                    setPaypalError(
                                        error?.message ||
                                        t("abonnement.downgrade.error")
                                    );
                                } finally {
                                    setPaypalLoading(false);
                                }
                            }}
                            onError={(error) => {
                                setPaypalError(
                                    error?.message ||
                                    t("abonnement.downgrade.error")
                                );
                            }}
                        />
                    </PayPalScriptProvider>

                    {paypalLoading && (
                        <p className="paypal-hint">
                            {t("abonnement.paypal.validating")}
                        </p>
                    )}
                </div>
            );
        }
        if (
            planKey === "cinephile" &&
            hasScheduledDowngrade
        ) {
            const scheduledDate =
                formattedAccessOverrideUntil ||
                formattedCurrentPeriodEnd;

            return (
                <p className="paypal-hint">
                    {t("abonnement.downgrade.scheduled")
                        .split("{{date}}")[0]}

                    <span className="paypal-hint__date">
                        {scheduledDate}
                    </span>

                    {t("abonnement.downgrade.scheduled")
                        .split("{{date}}")[1]}
                </p>
            );
        }
        if (selectedPlan !== planKey) return null;
        const isCurrentSubscriptionPlan =
            effectiveTier === planKey;
        if (hasPaidAccess && isCurrentSubscriptionPlan) {
            if (hasRemainingPaidPeriod) {
                return (
                    <p className="paypal-hint">
                        {t("abonnement.paypal.cancelledAccessUntil")
                            .split("{{date}}")[0]}

                        <span className="paypal-hint__date">
                            {formattedCurrentPeriodEnd}
                        </span>

                        {t("abonnement.paypal.cancelledAccessUntil")
                            .split("{{date}}")[1]}
                    </p>
                );
            }

            return (
                <div className="subscription-management">
                    <p className="paypal-hint">
                        {t("abonnement.paypal.alreadyActive")
                            .replace(
                                "{{plan}}",
                                planLabelMap[effectiveTier]
                            )}
                    </p>

                    {formattedCurrentPeriodEnd && (
                        <p className="paypal-hint">
                            {t("abonnement.cancelRenewal.nextRenewal")
                                .split("{{date}}")[0]}

                            <span className="paypal-hint__date">
                                {formattedCurrentPeriodEnd}
                            </span>

                            {t("abonnement.cancelRenewal.nextRenewal")
                                .split("{{date}}")[1]}
                        </p>
                    )}

                    <button
                        type="button"
                        className="btn-outline-abonnement"
                        onClick={openCancelConfirm}
                    >
                        {t("abonnement.cancelRenewal.button")}
                    </button>
                </div>
            );
        }

        if (!isLoggedIn) {
            return <p className="paypal-hint">{t("abonnement.paypal.loginToSubscribe")}</p>;
        }

        if (!paypalGateUnlocked) {
            return (
                <div className="paypal-temp-lock">
                    <button
                        type="button"
                        className="paypal-temp-lock__button"
                        onClick={openPaypalGate}
                    >
                        <strong>PayPal</strong>&nbsp; S'abonner
                    </button>
                    <p className="paypal-hint">
                        Abonnements temporairement réservés aux tests MovieFrenzy.
                    </p>
                </div>
            );
        }

        if (!paypalOptions) {
            return <p className="paypal-hint">{t("abonnement.paypal.loading")}</p>;
        }

        return (
            <div className="paypal-buttons">
                <PayPalScriptProvider options={paypalOptions}>
                    <PayPalButtons
                        style={{ layout: "vertical", shape: "pill", label: "subscribe" }}
                        forceReRender={[selectedPlan, billingPeriod, paypalClientId]}
                        createSubscription={() => createSubscription(planKey)}
                        onApprove={async (data) => {
                            setPaypalLoading(true);
                            setPaypalError(null);

                            try {
                                await confirmSubscription(data.subscriptionID);

                                window.dispatchEvent(
                                    new CustomEvent("mf-subscription-updated", {
                                        detail: {
                                            planId: planKey,
                                            status: "active",
                                        },
                                    })
                                );

                            } catch (e) {
                                setPaypalError(
                                    e.message || t("abonnement.paypal.genericError")
                                );
                            } finally {
                                setPaypalLoading(false);
                            }
                        }}

                        // onApprove={async (data) => {
                        //     console.log("[PAYPAL] onApprove reçu :", data);

                        //     setPaypalLoading(true);
                        //     setPaypalError(null);

                        //     try {
                        //         console.log(
                        //             "[PAYPAL] subscriptionID :",
                        //             data.subscriptionID
                        //         );

                        //         const result = await confirmSubscription(
                        //             data.subscriptionID
                        //         );

                        //         window.dispatchEvent(
                        //             new CustomEvent("mf-subscription-updated", {
                        //                 detail: {
                        //                     planId: planKey,
                        //                     status: "active",
                        //                 },
                        //             })
                        //         );

                        //         console.log(
                        //             "[PAYPAL] /confirm réussi :",
                        //             result
                        //         );
                        //     } catch (e) {
                        //         console.error(
                        //             "[PAYPAL] /confirm ERREUR :",
                        //             e
                        //         );

                        //         setPaypalError(
                        //             e.message ||
                        //             t("abonnement.paypal.genericError")
                        //         );
                        //     } finally {
                        //         setPaypalLoading(false);
                        //     }
                        // }}
                        onError={(err) => {
                            setPaypalError(err?.message || t("abonnement.paypal.genericError"));
                        }}
                    />
                </PayPalScriptProvider>
                {paypalLoading && <p className="paypal-hint">{t("abonnement.paypal.validating")}</p>}
            </div>
        );
    };

    return (
        <>
            <Helmet>
                <title>{t("subscriptionSeo.title")}</title>
                <meta
                    name="description"
                    content={t("subscriptionSeo.description")}
                />
                <link
                    rel="canonical"
                    href="https://moviefrenzy.ca/abonnement"
                />
            </Helmet>
            {/* Overlay 3D, visible uniquement sur cette page */}
            <AbonnementScene3D />
            <Navigation />
            <main
                className="abonnement-page"
                style={{
                    "--abn-bg-image": `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
                }}
            >
                {/* <div className="abonnement-nav">
                    <Navigation />
                </div> */}

                {/* HERO */}
                <section className="abonnement-hero">
                    <BackToTopButton />
                    <div className="abonnement-hero-content">
                        <h1>{t("abonnement.hero.title")}</h1>
                        <p className="abonnement-hero-subtitle">
                            {t("abonnement.hero.subtitle")}
                        </p>
                        <div className="abonnement-hero-cta">
                            <button className="btn-primary-abonnement" type="button" onClick={scrollToPlans}>
                                {t("abonnement.hero.startTrial")}
                            </button>
                            <p className="abonnement-hero-guarantee">
                                {t("abonnement.hero.guarantee")}
                            </p>
                        </div>
                    </div>

                    {/* Aperçu */}
                    <div className="abonnement-hero-visual">
                        <div className="abonnement-hero-card">
                            <p className="abonnement-hero-card-kicker">{t("abonnement.hero.premiumChangesTitle")}</p>
                            <ul className="abonnement-hero-card-list">
                                <li>{t("abonnement.hero.premiumChanges.zeroAds")}</li>
                                <li>{t("abonnement.hero.premiumChanges.curatedLists")}</li>
                                <li>{t("abonnement.hero.premiumChanges.activeCommunity")}</li>
                                <li>{t("abonnement.hero.premiumChanges.oneClickCancellation")}</li>
                            </ul>
                            <button className="btn-outline-abonnement" type="button" onClick={scrollToPlans}>
                                {t("abonnement.hero.viewPlans")}
                            </button>
                        </div>
                    </div>
                </section>

                {/* BÉNÉFICES */}
                <section className="abonnement-benefits">
                    <h2>{t("abonnement.benefits.title")}</h2>
                    <div className="abonnement-benefits-grid">
                        <article className="benefit-card abn-card abn-reveal abn-reveal--benefit">
                            <div className="abn-card-inner abn-wiggle">
                                <h3>{t("abonnement.benefits.saveTimeTitle")}</h3>
                                <p>{t("abonnement.benefits.saveTimeText")}</p>
                            </div>
                        </article>
                        <article className="benefit-card abn-card abn-reveal abn-reveal--benefit">
                            <div className="abn-card-inner abn-wiggle">
                                <h3>{t("abonnement.benefits.discoverGemsTitle")}</h3>
                                <p>{t("abonnement.benefits.discoverGemsText")}</p>
                            </div>
                        </article>
                        <article className="benefit-card abn-card abn-reveal abn-reveal--benefit">
                            <div className="abn-card-inner abn-wiggle">
                                <h3>{t("abonnement.benefits.clubExperienceTitle")}</h3>
                                <p>{t("abonnement.benefits.clubExperienceText")}</p>
                            </div>
                        </article>
                    </div>
                </section>

                {/* CE QUE TU OBTIENS */}
                <section className="abonnement-what-you-get">
                    <h2>{t("abonnement.whatYouGet.title")}</h2>
                    <ul className="what-you-get-list">
                        <li>{t("abonnement.whatYouGet.item1")}</li>
                        <li>{t("abonnement.whatYouGet.item2")}</li>
                        <li>{t("abonnement.whatYouGet.item3")}</li>
                        <li>{t("abonnement.whatYouGet.item4")}</li>
                    </ul>
                </section>

                {/* PRICING – 3 PLANS */}
                <section className="abonnement-pricing" id="plans">
                    <div className="abonnement-pricing-header">
                        <h2>{t("abonnement.billing.choosePlan")}</h2>
                        <div className="billing-toggle" role="group" aria-label={t("abonnement.billing.ariaLabel")}>
                            <button
                                className={billingPeriod === "monthly" ? "active" : ""}
                                onClick={() => setBillingPeriod("monthly")}
                                type="button"
                                aria-pressed={billingPeriod === "monthly"}
                            >
                                {t("abonnement.billing.monthly")}
                            </button>
                            <button
                                className={billingPeriod === "quarterly" ? "active" : ""}
                                onClick={() => setBillingPeriod("quarterly")}
                                type="button"
                                aria-pressed={billingPeriod === "quarterly"}
                            >
                                {t("abonnement.billing.quarterly")}
                            </button>
                            <button
                                className={billingPeriod === "yearly" ? "active" : ""}
                                onClick={() => setBillingPeriod("yearly")}
                                type="button"
                                aria-pressed={billingPeriod === "yearly"}
                            >
                                {t("abonnement.billing.yearly")}
                            </button>
                        </div>
                    </div>
                    {subscription && (
                        <p className="pricing-status">
                            {t("abonnement.status.current")
                                .replace(
                                    "{{plan}}",
                                    planLabelMap[effectiveTier] || planLabelMap.free
                                )
                                .replace(
                                    "{{status}}",
                                    subscriptionStatusLabelMap[subscription.status] ||
                                    subscription.status ||
                                    subscriptionStatusLabelMap.none
                                )}
                        </p>
                    )}
                    {paypalError && (
                        <p className="pricing-error">
                            {paypalError}
                        </p>
                    )}
                    <div className="pricing-grid">
                        {/* Gratuit */}
                        <article
                            className={`pricing-card pricing-card--free ${selectedPlan === "free" ? "is-selected" : ""
                                } ${hasPaidAccess ? "is-disabled" : ""}`}
                            tabIndex={hasPaidAccess ? -1 : 0}
                            onClick={(e) => {
                                if (hasPaidAccess) return;

                                setSelectedPlan("free");
                                e.currentTarget.focus();
                            }}
                            onKeyDown={(e) => {
                                if (hasPaidAccess) return;

                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setSelectedPlan("free");
                                }
                            }}
                            aria-label={t("abonnement.plans.free.ariaLabel")}
                        >
                            <h3>{t("abonnement.plans.free.name")}</h3>

                            <p className="pricing-price">
                                {PRICING.free[billingPeriod].toFixed(2)} $ /{" "}
                                {PERIOD_META[billingPeriod].label}
                            </p>

                            <ul className="pricing-features">
                                <li>{t("abonnement.plans.free.feature1")}</li>
                                <li>{t("abonnement.plans.free.feature2")}</li>
                                <li>{t("abonnement.plans.free.feature3")}</li>
                            </ul>

                            <button
                                className="btn-outline-abonnement"
                                type="button"
                                disabled={hasPaidAccess}
                                onClick={(e) => {
                                    e.stopPropagation();

                                    if (hasPaidAccess) return;

                                    setSelectedPlan("free");
                                }}
                            >
                                {hasPaidAccess
                                    ? t("abonnement.plans.free.availableAfterPaidPeriod")
                                    : t("abonnement.plans.free.button")}
                            </button>
                        </article>

                        {/* Plan recommandé */}
                        <article
                            className={`pricing-card pricing-card-featured pricing-card--featured ${selectedPlan === "cinephile" ? "is-selected" : ""}`}
                            tabIndex={0}
                            onClick={(e) => {
                                if (e.target.closest("button")) return;

                                requestPlanSelection("cinephile");
                                e.currentTarget.focus();
                            }}
                            onKeyDown={(e) => {
                                if (e.target !== e.currentTarget) return;

                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    requestPlanSelection("cinephile");
                                }
                            }}
                            aria-label={t("abonnement.plans.cinephile.ariaLabel")}
                        >
                            <div className="pricing-badge">{t("abonnement.plans.cinephile.recommended")}</div>
                            <h3>{t("abonnement.plans.cinephile.name")}</h3>
                            <p className="pricing-price">
                                {PRICING.cinephile[billingPeriod].toFixed(2)} $ / {PERIOD_META[billingPeriod].label}
                            </p>
                            {billingPeriod !== "monthly" && (
                                <p className="pricing-note">
                                    {t("abonnement.billing.approxPerMonth").replace(
                                        "{{price}}",
                                        (
                                            PRICING.cinephile[billingPeriod] /
                                            PERIOD_META[billingPeriod].months
                                        ).toFixed(2)
                                    )}
                                </p>
                            )}
                            <ul className="pricing-features">
                                <li>{t("abonnement.plans.cinephile.feature1")}</li>
                                <li>{t("abonnement.plans.cinephile.feature2")}</li>
                                <li>{t("abonnement.plans.cinephile.feature3")}</li>
                                <li>{t("abonnement.plans.cinephile.feature4")}</li>
                            </ul>
                            <button
                                className="btn-primary-abonnement"
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    requestPlanSelection("cinephile");
                                }}
                            >
                                {t("abonnement.plans.cinephile.button")}
                            </button>
                            <p className="pricing-note">
                                {t("abonnement.plans.cinephile.founderNote")}
                            </p>
                            {renderPayPalButtons("cinephile")}
                        </article>

                        {/* Fanatique */}
                        <article
                            className={`pricing-card pricing-card--fanatic ${selectedPlan === "fanatic" ? "is-selected" : ""}`}
                            tabIndex={0}
                            onClick={(e) => {
                                if (e.target.closest("button")) return;

                                requestPlanSelection("fanatic");
                                e.currentTarget.focus();
                            }}
                            onKeyDown={(e) => {
                                if (e.target !== e.currentTarget) return;

                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    requestPlanSelection("fanatic");
                                }
                            }}
                            aria-label={t("abonnement.plans.fanatic.ariaLabel")}
                        >
                            <h3>{t("abonnement.plans.fanatic.name")}</h3>
                            <p className="pricing-price">
                                {PRICING.fanatic[billingPeriod].toFixed(2)} $ / {PERIOD_META[billingPeriod].label}
                            </p>
                            {billingPeriod !== "monthly" && (
                                <p className="pricing-note">
                                    {t("abonnement.billing.approxPerMonth").replace(
                                        "{{price}}",
                                        (
                                            PRICING.fanatic[billingPeriod] /
                                            PERIOD_META[billingPeriod].months
                                        ).toFixed(2)
                                    )}
                                </p>
                            )}
                            <ul className="pricing-features">
                                <li>{t("abonnement.plans.fanatic.feature1")}</li>
                                <li>{t("abonnement.plans.fanatic.feature2")}</li>
                                <li>{t("abonnement.plans.fanatic.feature3")}</li>
                                <li>{t("abonnement.plans.fanatic.feature4")}</li>
                            </ul>
                            {!(
                                hasPaidAccess &&
                                effectiveTier === "fanatic"
                            ) && (
                                    <button
                                        className="btn-outline-abonnement"
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            requestPlanSelection("fanatic");
                                        }}
                                    >
                                        {t("abonnement.plans.fanatic.button")}
                                    </button>
                                )}
                            {renderPayPalButtons("fanatic")}
                        </article>
                    </div>
                </section>

                {/* COMMUNAUTÉ / NEWSLETTER */}
                <section className="abonnement-community">
                    <h2>{t("abonnement.community.title")}</h2>
                    <p>{t("abonnement.community.text")}</p>
                    <form className="abonnement-newsletter-form" onSubmit={(e) => e.preventDefault()}>
                        <label className="sr-only" htmlFor="abonnement-newsletter-email">
                            {t("abonnement.community.emailLabel")}
                        </label>
                        <input
                            id="abonnement-newsletter-email"
                            type="email"
                            placeholder={t("abonnement.community.emailPlaceholder")}
                            className="newsletter-input"
                            autoComplete="email"
                            required
                        />
                        <button type="submit" className="btn-primary-abonnement">
                            {t("abonnement.community.signup")}
                        </button>
                        {/* <div>
                            <button type="button" onClick={testSubscriptionSecurity}>
                                TEST SÉCURITÉ SUBSCRIPTION
                            </button>
                        </div> */}
                    </form>
                </section>

                {/* FAQ */}
                <section className="abonnement-faq">
                    <h2>{t("abonnement.faq.title")}</h2>
                    <div className="faq-item abn-reveal abn-reveal--faq">
                        <div>
                            <h3>{t("abonnement.faq.cancelQuestion")}</h3>
                            <p>{t("abonnement.faq.cancelAnswer")}</p>
                        </div>
                    </div>
                    <div className="faq-item abn-reveal abn-reveal--faq">
                        <div>
                            <h3>{t("abonnement.faq.paymentQuestion")}</h3>
                            <p>{t("abonnement.faq.paymentAnswer")}</p>
                        </div>
                    </div>
                    <div className="faq-item abn-reveal abn-reveal--faq">
                        <div>
                            <h3>{t("abonnement.faq.dissatisfiedQuestion")}</h3>
                            <p>{t("abonnement.faq.dissatisfiedAnswer")}</p>
                        </div>
                    </div>
                </section>

                <Footer />
            </main>

            {showCancelConfirm && (
                <div
                    className="plan-change-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cancel-renewal-modal-title"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) {
                            closeCancelConfirm();
                        }
                    }}
                >
                    <div className="plan-change-modal__card">
                        <h2 id="cancel-renewal-modal-title">
                            {t("abonnement.cancelRenewal.title")}
                        </h2>

                        <p>
                            {t("abonnement.cancelRenewal.intro")
                                .replace(
                                    "{{plan}}",
                                    planLabelMap[effectiveTier]
                                )}
                        </p>

                        {formattedCurrentPeriodEnd && (
                            <p className="plan-change-modal__period">
                                {t("abonnement.cancelRenewal.accessUntil")
                                    .split("{{date}}")[0]}

                                <strong className="plan-change-modal__date">
                                    {formattedCurrentPeriodEnd}
                                </strong>

                                {t("abonnement.cancelRenewal.accessUntil")
                                    .split("{{date}}")[1]}
                            </p>
                        )}

                        <p className="plan-change-modal__warning">
                            {t("abonnement.cancelRenewal.warning")}
                        </p>

                        <div className="plan-change-modal__actions">
                            <button
                                type="button"
                                className="plan-change-modal__cancel"
                                onClick={closeCancelConfirm}
                                disabled={cancelLoading}
                            >
                                {t("abonnement.cancelRenewal.keep")}
                            </button>

                            <button
                                type="button"
                                className="plan-change-modal__confirm"
                                onClick={cancelCurrentSubscription}
                                disabled={cancelLoading}
                            >
                                {cancelLoading
                                    ? t("abonnement.cancelRenewal.processing")
                                    : t("abonnement.cancelRenewal.confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDowngradeConfirm && (
                <div
                    className="plan-change-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="downgrade-modal-title"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) {
                            closeDowngradeConfirm();
                        }
                    }}
                >
                    <div className="plan-change-modal__card">
                        <h2 id="downgrade-modal-title">
                            {t("abonnement.downgrade.title")}
                        </h2>

                        <p>
                            {t("abonnement.downgrade.intro")}
                        </p>

                        {formattedCurrentPeriodEnd && (
                            <p className="plan-change-modal__period">
                                {t("abonnement.downgrade.currentAccessUntil")
                                    .split("{{date}}")[0]}

                                <strong className="plan-change-modal__date">
                                    {formattedCurrentPeriodEnd}
                                </strong>

                                {t("abonnement.downgrade.currentAccessUntil")
                                    .split("{{date}}")[1]}
                            </p>
                        )}

                        <p className="plan-change-modal__warning">
                            {t("abonnement.downgrade.billingWarning")}
                        </p>

                        <div className="plan-change-modal__actions">
                            <button
                                type="button"
                                className="plan-change-modal__cancel"
                                onClick={closeDowngradeConfirm}
                                disabled={downgradeLoading}
                            >
                                {t("abonnement.downgrade.keepFanatic")}
                            </button>

                            <button
                                type="button"
                                className="plan-change-modal__confirm"
                                onClick={acceptDowngrade}
                                disabled={downgradeLoading}
                            >
                                {downgradeLoading
                                    ? t("abonnement.downgrade.processing")
                                    : t("abonnement.downgrade.confirmCinephile")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPlanChangeConfirm && (
                <div
                    className="plan-change-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="plan-change-modal-title"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) {
                            closePlanChangeConfirm();
                        }
                    }}
                >
                    <div className="plan-change-modal__card">
                        <h2 id="plan-change-modal-title">
                            {t("abonnement.planChange.title")}
                        </h2>

                        <p>
                            {t("abonnement.planChange.intro")}
                        </p>

                        {formattedCurrentPeriodEnd && (
                            <p className="plan-change-modal__period">
                                {t("abonnement.planChange.currentAccessUntil")
                                    .split("{{date}}")[0]}

                                <strong className="plan-change-modal__date">
                                    {formattedCurrentPeriodEnd}
                                </strong>

                                {t("abonnement.planChange.currentAccessUntil")
                                    .split("{{date}}")[1]}
                            </p>
                        )}

                        <p className="plan-change-modal__warning">
                            {t("abonnement.planChange.billingWarning")}
                        </p>

                        <div className="plan-change-modal__actions">
                            <button
                                type="button"
                                className="plan-change-modal__cancel"
                                onClick={closePlanChangeConfirm}
                            >
                                {t("abonnement.planChange.keepCinephile")}
                            </button>

                            <button
                                type="button"
                                className="plan-change-modal__confirm"
                                onClick={confirmPlanChange}
                            >
                                {t("abonnement.planChange.confirmFanatic")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPaypalGate && (
                <div
                    className="paypal-temp-gate"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="paypal-temp-gate-title"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) closePaypalGate();
                    }}
                >
                    <div className="paypal-temp-gate__card">
                        <h2 id="paypal-temp-gate-title">
                            🔒 Accès temporaire aux abonnements
                        </h2>

                        <p>
                            Les offres MovieFrenzy sont encore en préparation.
                            L'accès PayPal est réservé aux tests autorisés.
                        </p>

                        <div className="paypal-temp-gate__form">
                            <input
                                type="password"
                                value={paypalGatePassword}
                                onChange={(e) => {
                                    setPaypalGatePassword(e.target.value);
                                    setPaypalGateError(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        verifyPaypalGate();
                                    }
                                }}
                                placeholder="Mot de passe"
                                autoComplete="off"
                                autoFocus
                            />

                            <button
                                type="button"
                                onClick={verifyPaypalGate}
                                disabled={!paypalGatePassword.trim() || paypalGateLoading}
                            >
                                {paypalGateLoading ? "Vérification..." : "Déverrouiller"}
                            </button>
                        </div>

                        {paypalGateError && (
                            <p className="paypal-temp-gate__error">
                                {paypalGateError}
                            </p>
                        )}

                        <button
                            type="button"
                            className="paypal-temp-gate__cancel"
                            onClick={closePaypalGate}
                            disabled={paypalGateLoading}
                        >
                            Annuler
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Abonnement;